import { apiClient } from './api';
import type {
  CreateGoalApiRequest,
  CreateGoalApiResponse,
  UpdateGoalApiRequest,
  GetGoalsApiResponse,
  GetCompanyHierarchyApiRequest,
  GetCompanyHierarchyApiResponse,
  CreateTaskApiResponse,
  CreateTaskRequestBody,
  UpdateTaskApiRequest,
  IssueType,
  GetUserInteraTasks,
  UpdateTaskApiResponse,
  UpdateIssueStatusApiRequest,
  UpdateOccurrenceApiRequest,
  DeleteOccurrenceApiRequest,
  DeleteIssueApiResponse,
  ImportMsProjectApiResponse,
  ExportGoalToExcelApiRequest,
} from '@momentum/types';

export const issueService = {
  async createGoal(data: CreateGoalApiRequest): Promise<CreateGoalApiResponse> {
    return (await apiClient.post('/issues/goals', data)).data;
  },

  async createTask(
    data: CreateTaskRequestBody
  ): Promise<CreateTaskApiResponse> {
    return (await apiClient.post('/issues/tasks', data)).data;
  },

  async updateGoal(
    id: string,
    data: UpdateGoalApiRequest
  ): Promise<CreateGoalApiResponse> {
    return (await apiClient.patch(`/issues/goals/${id}`, data)).data;
  },

  async getAllGoals(types: IssueType[]): Promise<GetGoalsApiResponse> {
    const params = new URLSearchParams();

    if (Array.isArray(types)) {
      for (const t of types) params.append('types', t);
    }
    return (await apiClient.get(`/issues/goals?${params.toString()}`)).data;
  },

  async getMyGoals(types: IssueType[]): Promise<GetGoalsApiResponse> {
    const params = new URLSearchParams();

    if (Array.isArray(types)) {
      for (const t of types) params.append('types', t);
    }
    return (await apiClient.get(`/issues/goals/my-goals?${params.toString()}`))
      .data;
  },

  async getCompanyHierarchy(
    params: GetCompanyHierarchyApiRequest
  ): Promise<GetCompanyHierarchyApiResponse> {
    const queryParams = new URLSearchParams();

    if (params.issueTypes && params.issueTypes.length > 0) {
      params.issueTypes.forEach((type) => {
        queryParams.append('issueTypes', type);
      });
    }

    if (params.employeeId) {
      queryParams.append('employeeId', params.employeeId);
    }

    return (
      await apiClient.get(`/issues/company/hierarchy?${queryParams.toString()}`)
    ).data;
  },

  async getUserTasks(isInteraTasks: boolean): Promise<GetUserInteraTasks> {
    const response = await apiClient.get(
      `/issues/tasks?isInteraTasks=${isInteraTasks.toString()}`
    );
    return response.data;
  },

  async updateTask(
    id: string,
    data: Omit<UpdateTaskApiRequest, '_id'>
  ): Promise<UpdateTaskApiResponse> {
    return (await apiClient.patch(`/issues/tasks/${id}`, data)).data;
  },

  async updateTaskStatus(
    id: string,
    data: UpdateIssueStatusApiRequest
  ): Promise<UpdateTaskApiResponse> {
    const response = await apiClient.patch(`/issues/tasks/${id}`, data);
    return response.data;
  },

  async updateOccurrenceHistory(
    taskId: string,
    data: UpdateOccurrenceApiRequest
  ): Promise<UpdateTaskApiResponse> {
    const response = await apiClient.patch(
      `/issues/tasks/${taskId}/occurrence-history`,
      data
    );
    return response.data;
  },

  async deleteOccurrence(
    taskId: string,
    data: DeleteOccurrenceApiRequest
  ): Promise<UpdateTaskApiResponse> {
    const response = await apiClient.delete(
      `/issues/tasks/${taskId}/occurrence-history`,
      { data }
    );
    return response.data;
  },

  async deleteIssue(id: string): Promise<DeleteIssueApiResponse> {
    const response = await apiClient.delete(`/issues/${id}`);
    return response.data;
  },

  async importMsProject(file: File): Promise<ImportMsProjectApiResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return (
      await apiClient.post('/issues/import/msproject', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60_000,
      })
    ).data;
  },

  async exportGoalToExcel(
    goalId: string,
    data: ExportGoalToExcelApiRequest
  ): Promise<Blob> {
    const response = await apiClient.post(
      `/issues/goals/${goalId}/_export/excel`,
      data,
      {
        responseType: 'blob',
      }
    );

    return response.data;
  },
};
